import React, { useEffect, useState } from 'react';
import { Flex, Pane, Text, TextInput, Toast } from '@leverege/ui-elements';
import P from '@leverege/promise';
import { LoadingView } from '@leverege/molten/views';
import InputGroup from '../components/InputGroup';
import C from '../Constants';
import S from './BulkSaleInformation.css';
import { getTagCountOfVehicle } from '../../../util/Interface';
import { getPairedVehicleSaleWarningMessage } from '../Util';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const INPUTS_BY_SALE_TYPE = {
  [C.SALE_TYPES.VEHICLE_ONLY]: ['salesperson'],
  [C.SALE_TYPES.VEHICLE_WHOLESALE]: ['customerName', 'salesperson'],
  [C.SALE_TYPES.DEALER_TRADE]: ['customerName', 'salesperson']
};
const SALE_NAMES_BY_SALE_TYPE = {
  [C.SALE_TYPES.VEHICLE_ONLY]: [C.SALE_NAMES.VEHICLE_ONLY],
  [C.SALE_TYPES.VEHICLE_WHOLESALE]: [C.SALE_NAMES.VEHICLE_WHOLESALE],
  [C.SALE_TYPES.DEALER_TRADE]: [C.SALE_NAMES.DEALER_TRADE]
};

/**
 * Display bulk sale information (to be used in the content section of a dialog).
 */
export default function BulkSaleInformation(props) {
  const {
    vehicles,
    saleType,
    customerName,
    salesperson,
    onChange
  } = props;
  const [warningState, setWarningState] = useState({
    pairedVehicles: [],
    warning: null,
    installLocation: null,
    warningPills: null
  });

  // Display singular/plural message depending on the vehicle amount.
  let message;
  if (vehicles.length === 1) {
    message = `This vehicle will be removed from your inventory and marked as a ${SALE_NAMES_BY_SALE_TYPE[saleType]} sale.`; // eslint-disable-line max-len
  } else {
    message = `${vehicles.length} vehicles will be removed from your inventory and marked as ${SALE_NAMES_BY_SALE_TYPE[saleType]} sales.`; // eslint-disable-line max-len
  }
  const pairedVehiclesData = {
    vehiclesWithTagsAndLocators: 0,
    vehiclesWithTagsOnly: 0,
    vehiclesWithLocatorsOnly: 0,
    pairedVehicles: [],
    tagCount: 0
  };
  const getWarnings = pairedVehicles => {
    let pairedTo, installLocation, warning;

    // Display singular/plural warning message depending on the paired vehicle amount.
    if (pairedVehicles.length === 1) {
      ({
        warning,
        pairedTo
      } = getPairedVehicleSaleWarningMessage(pairedVehiclesData));
      // Display install location message if single paired vehicle with locator.
      if (pairedTo !== C.VEHICLE_PAIRED_TO.TAG) {
        installLocation = pairedVehicles[0].data.data.locator?.data?.installLocation;
      }
    } else {
      ({
        warning,
        pairedTo
      } = getPairedVehicleSaleWarningMessage(pairedVehiclesData, true));
    }
    const locatorPill = /*#__PURE__*/_jsx(Text, {
      variant: "pillError",
      className: S.pill,
      imageOpts: {
        className: S.icon
      },
      icon: "https://storage.googleapis.com/recovr-ui-assets/icon-hardware-tracker.svg",
      children: "Remove Locator"
    });
    const tagPill = /*#__PURE__*/_jsx(Text, {
      variant: "pillError",
      className: S.pill,
      imageOpts: {
        className: S.icon
      },
      icon: "https://storage.googleapis.com/recovr-ui-assets/icon-tag.svg",
      children: `Remove ${pairedVehiclesData.tagCount} Tag${pairedVehiclesData.tagCount > 1 ? 's' : ''} from Keys`
    });
    const warningDictionary = {
      [C.VEHICLE_PAIRED_TO.TAG]: tagPill,
      [C.VEHICLE_PAIRED_TO.LOCATOR]: locatorPill,
      [C.VEHICLE_PAIRED_TO.TAG_AND_LOCATOR]: [locatorPill, tagPill]
    };
    const warningPills = warningDictionary[pairedTo];
    setWarningState({
      pairedVehicles,
      warning,
      installLocation,
      warningPills
    });
  };
  useEffect(() => {
    const closeLoading = LoadingView.showDialog('Fetching Vehicle Data');
    async function getPairedVehiclesData() {
      await P.mapLimit(vehicles, async vehicle => {
        const tagCount = await getTagCountOfVehicle({
          vehicleId: vehicle.data.id,
          locationId: vehicle.data.data.location?.id,
          companyId: vehicle.data.data.company?.id
        });
        const locator = vehicle.data.data.locator?.id || null;
        if (tagCount > 0 || locator) {
          pairedVehiclesData.pairedVehicles.push(vehicle);
          pairedVehiclesData.tagCount += tagCount;
          if (tagCount > 0 && locator) {
            pairedVehiclesData.vehiclesWithTagsAndLocators++;
          } else if (tagCount > 0) {
            pairedVehiclesData.vehiclesWithTagsOnly++;
          } else {
            pairedVehiclesData.vehiclesWithLocatorsOnly++;
          }
        }
        if (tagCount === -1) {
          Toast.error(`Error when checking if tags are paired to keys of ${vehicle.data.name}.\n
          Make sure to unpair all tags from paired keys before selling the vehicle.`);
        }
      });
      if (pairedVehiclesData.pairedVehicles.length > 0) {
        getWarnings(pairedVehiclesData.pairedVehicles);
      }
      closeLoading();
    }
    getPairedVehiclesData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const showCustomerNameInput = INPUTS_BY_SALE_TYPE[saleType].includes('customerName');
  const showSalespersonInput = INPUTS_BY_SALE_TYPE[saleType].includes('salesperson');
  return /*#__PURE__*/_jsxs(Pane, {
    layout: "flex:colL",
    children: [/*#__PURE__*/_jsx(Text, {
      variant: "body",
      children: message
    }), showCustomerNameInput && /*#__PURE__*/_jsx(InputGroup, {
      eventData: "customerName",
      onChange: onChange,
      editor: TextInput,
      value: customerName,
      title: "Customer Name"
    }), showSalespersonInput && /*#__PURE__*/_jsx(InputGroup, {
      eventData: "salesperson",
      onChange: onChange,
      editor: TextInput,
      value: salesperson,
      title: "Salesperson"
    }), warningState.pairedVehicles.length > 0 && /*#__PURE__*/_jsxs(Flex, {
      variant: "colL",
      children: [/*#__PURE__*/_jsx("div", {
        className: S.divider
      }), /*#__PURE__*/_jsxs(Pane, {
        className: S.container,
        layout: "flex:col",
        align: "start",
        children: [/*#__PURE__*/_jsxs(Flex, {
          variant: "row",
          className: S.pillRow,
          children: [" ", warningState.warningPills]
        }), /*#__PURE__*/_jsxs(Text, {
          className: S.warningText,
          children: [/*#__PURE__*/_jsx("b", {
            children: "Warning: "
          }), warningState.warning]
        })]
      })]
    }), warningState.installLocation?.length > 0 && /*#__PURE__*/_jsx(Pane, {
      className: S.container,
      children: /*#__PURE__*/_jsxs(Flex, {
        variant: "row",
        justify: "space-between",
        children: [/*#__PURE__*/_jsx(Text, {
          className: S.name,
          children: "Install Location"
        }), /*#__PURE__*/_jsx(Text, {
          className: S.value,
          children: warningState.installLocation
        })]
      })
    })]
  });
}